import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  input,
} from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  ItdrService,
  IdentityScoreData,
  ExecutiveMetrics,
  ExposureMetrics,
  DetectionTrendData,
  PrivilegedAccessSummary,
  IdentityRiskSummary,
  ScoreSeverityTier,
} from '../../services/identity-security.service';
import { HeroBannerComponent } from './components/hero-banner/hero-banner.component';
import { PostureBreakdownComponent } from './components/posture-breakdown/posture-breakdown.component';
import { DetectionTrendComponent } from './components/detection-trend/detection-trend.component';
import { PrivilegedAccessComponent } from './components/privileged-access/privileged-access.component';
import { XdrCorrelationComponent } from './components/xdr-correlation/xdr-correlation.component';
import {
  IdentityEntityDialogComponent,
  IdentityEntityDialogConfig,
} from '../identity-entity-dialog/identity-entity-dialog.component';
type KpiType = 'high-risk' | 'privileged-at-risk' | 'leaked-credentials' | 'monitored-users';

@Component({
  selector: 'app-itdr-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    HeroBannerComponent,
    PostureBreakdownComponent,
    DetectionTrendComponent,
    PrivilegedAccessComponent,
    XdrCorrelationComponent,
    IdentityEntityDialogComponent,
  ],
  templateUrl: './itdr-dashboard.component.html',
  styleUrl: './itdr-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItdrDashboardComponent {
  private itdrService = inject(ItdrService);

  /** Active organization, from `?organizationId=` on the page. */
  public organizationId = input<number | null>(null);

  // Per-widget loading signals
  public scoreLoading = signal<boolean>(true);
  public metricsLoading = signal<boolean>(true);
  public privilegedLoading = signal<boolean>(true);
  public trendLoading = signal<boolean>(true);
  public risksLoading = signal<boolean>(true);

  // Derived loading for components needing multiple endpoints
  public heroBannerLoading = computed(() => this.scoreLoading() || this.metricsLoading());

  // Data signals
  /**
   * The score as the backend composed it, used directly.
   *
   * Severity overrides are no longer applied here. The backend recomposes the score
   * on every request with the customer's overrides and acceptances already folded in,
   * so recomputing locally would produce a second answer to the same question — and
   * it did: summing rounded per-check impacts drifts from dividing the weights once,
   * so the two disagreed by a point or two.
   */
  private rawScore = signal<IdentityScoreData | null>(null);
  public identityScore = computed<IdentityScoreData | null>(() => this.rawScore());
  public executiveMetrics = signal<ExecutiveMetrics | null>(null);
  public exposureMetrics = signal<ExposureMetrics | null>(null);
  public privilegedAccess = signal<PrivilegedAccessSummary | null>(null);
  public detectionTrend = signal<DetectionTrendData | null>(null);
  public identityRisks = signal<IdentityRiskSummary[]>([]);
  /** Set when nothing was read, so the widget does not report an empty list as an answer. */
  public risksUnavailable = signal<boolean>(false);

  // View Details dialog state — single shared dialog driven by config signal.
  public entityDialogConfig = signal<IdentityEntityDialogConfig | null>(null);
  public showEntityDialog = signal<boolean>(false);

  constructor() {
    // switchMap, not a plain subscribe per change: switching organizations while a
    // request is in flight must cancel it. Otherwise a slow response for the previous
    // organization can land after the new one and paint another customer's admin counts
    // under the current customer's name.
    toObservable(this.organizationId)
      .pipe(
        tap(() => {
          this.privilegedAccess.set(null);
          this.privilegedLoading.set(true);
        }),
        switchMap((organizationId) => {
          if (organizationId == null) {
            return of(null);
          }
          return this.itdrService.getPrivilegedAccess(organizationId).pipe(
            // The endpoint answers 204 (not an error) for an organization with no data
            // yet, so reaching here means a real failure. Keep it off the stream so one
            // bad response does not stop later organization switches from loading.
            catchError((err) => {
              console.error('Failed to load privileged access:', err);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((res) => {
        // Null covers all three: no organization selected, 204, and a failed request.
        // The strip hides itself rather than claiming zero privileged accounts.
        this.privilegedAccess.set(res);
        this.privilegedLoading.set(false);
      });

    // Same shape for the score. Kept as its own pipeline rather than folded into the
    // one above so each widget keeps its own loading state and one slow endpoint does
    // not hold the other back.
    toObservable(this.organizationId)
      .pipe(
        tap(() => {
          this.rawScore.set(null);
          this.exposureMetrics.set(null);
          this.scoreLoading.set(true);
        }),
        switchMap((organizationId) => {
          if (organizationId == null) {
            return of(null);
          }
          return this.itdrService.getScore(organizationId).pipe(
            catchError((err) => {
              console.error('Failed to load ITDR score:', err);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((res) => {
        this.rawScore.set(res?.identity_score ?? null);
        this.exposureMetrics.set(res?.exposure_metrics ?? null);
        this.scoreLoading.set(false);
      });

    // The four headline tiles.
    toObservable(this.organizationId)
      .pipe(
        tap(() => {
          this.executiveMetrics.set(null);
          this.metricsLoading.set(true);
        }),
        switchMap((organizationId) => {
          if (organizationId == null) {
            return of(null);
          }
          return this.itdrService.getExecutiveMetrics(organizationId).pipe(
            catchError((err) => {
              console.error('Failed to load executive metrics:', err);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((res) => {
        this.executiveMetrics.set(res);
        this.metricsLoading.set(false);
      });

    toObservable(this.organizationId)
      .pipe(
        tap(() => {
          this.detectionTrend.set(null);
          this.trendLoading.set(true);
        }),
        switchMap((organizationId) => {
          if (organizationId == null) {
            return of(null);
          }
          return this.itdrService.getDetectionTrend(organizationId).pipe(
            catchError((err) => {
              console.error('Failed to load detection trend:', err);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((res) => {
        this.detectionTrend.set(res);
        this.trendLoading.set(false);
      });

    toObservable(this.organizationId)
      .pipe(
        tap(() => {
          this.identityRisks.set([]);
          this.risksLoading.set(true);
        }),
        switchMap((organizationId) => {
          if (organizationId == null) {
            return of(null);
          }
          return this.itdrService.getIdentityRisks(organizationId).pipe(
            catchError((err) => {
              console.error('Failed to load identity risks:', err);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((res) => {
        // Null covers no organization, 204 (no checks in Gold) and a failed request:
        // nobody looked, which is not the same as nobody being escalated.
        this.identityRisks.set(res ?? []);
        this.risksUnavailable.set(res === null);
        this.risksLoading.set(false);
      });
  }

  /**
   * Customer reassigned a check's severity tier in the score dialog.
   *
   * Persist, then reload. The backend stores the override, recomputes the score with
   * the new weighting and returns it, so the number on screen is the one it stands
   * by — rather than a local approximation that the next page load would contradict.
   */
  public onTierChange(event: { checkId: string; severity: ScoreSeverityTier }): void {
    const organizationId = this.organizationId();
    if (organizationId == null) return;

    this.itdrService
      .setSeverityOverride(event.checkId, organizationId, event.severity)
      .subscribe({
        next: () => this.reloadScore(organizationId),
        error: (err) => console.error('Failed to save severity override:', err),
      });
  }

  /** Refetch the score after a write that changes it. */
  private reloadScore(organizationId: number): void {
    this.itdrService
      .getScore(organizationId)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        this.rawScore.set(res?.identity_score ?? null);
        this.exposureMetrics.set(res?.exposure_metrics ?? null);
      });
  }

  public onOpenKpiDetail(kpi: KpiType): void {
    const organizationId = this.organizationId();
    if (organizationId == null) return;

    const configs: Record<KpiType, IdentityEntityDialogConfig> = {
      'high-risk': {
        title: 'High-Risk Identities',
        subtitle: 'Identities flagged as high risk in your environment right now.',
        apiUrl: this.itdrService.getDetailsUrl('high-risk-identities', organizationId),
        columns: [
          { field: 'identity', title: 'Identity', width: 220 },
          { field: 'identity_type', title: 'Type', width: 140 },
          // Entra reports a risk level, not a score. A number here would be invented.
          { field: 'risk_level', title: 'Risk Level', width: 120, type: 'badge' },
          { field: 'risk_signals', title: 'Signals', width: 260 },
          { field: 'last_seen', title: 'Last Seen', width: 180, type: 'date' },
        ],
      },
      'privileged-at-risk': {
        title: 'Privileged Accounts at Risk',
        subtitle: 'Admin-tier accounts with one or more risk signals.',
        apiUrl: this.itdrService.getDetailsUrl('privileged-at-risk', organizationId),
        columns: [
          { field: 'identity', title: 'Identity', width: 220 },
          { field: 'role', title: 'Roles', width: 240 },
          { field: 'mfa_status', title: 'MFA', width: 140 },
          // "Last admin action" needs the audit logs, which are out of v1, and Entra
          // reports a risk level rather than a score.
          { field: 'risk_level', title: 'Risk Level', width: 120, type: 'badge' },
          // Why the admin is at risk. The table stated a level and gave no reason for
          // it; the subtitle above already promises "one or more risk signals".
          { field: 'risk_signals', title: 'Signals', width: 260 },
        ],
      },
      'leaked-credentials': {
        title: 'Leaked Credentials',
        subtitle: 'Identities with a leaked credential that has not been rotated since detection.',
        apiUrl: this.itdrService.getDetailsUrl('leaked-credentials', organizationId),
        columns: [
          { field: 'identity', title: 'Identity', width: 220 },
          { field: 'risk_level', title: 'Risk Level', width: 120, type: 'badge' },
          { field: 'detected_at', title: 'Detected', width: 180, type: 'date' },
          {
            field: 'last_password_change',
            title: 'Last Password Change',
            width: 200,
            // The row stays in the list when the directory never reported a password
            // change, so the cell has to say we do not know. Left blank it reads as
            // "never changed it", next to a days-unrotated count that keeps rising.
            type: 'date',
            emptyText: 'Unknown',
          },
          { field: 'days_unrotated', title: 'Days Unrotated', width: 140, type: 'number' },
        ],
      },
      'monitored-users': {
        title: 'Monitored Identities',
        subtitle: 'All identities the ITDR engine is currently watching.',
        // Every account in the directory: 12,905 in the largest production tenant,
        // so this one pages on the server instead of arriving in one response.
        serverSide: true,
        apiUrl: this.itdrService.getDetailsUrl('monitored-users', organizationId),
        columns: [
          { field: 'identity', title: 'Identity', width: 220 },
          { field: 'department', title: 'Department', width: 160 },
          { field: 'last_signin', title: 'Last Sign-In', width: 180, type: 'date' },
          { field: 'mfa_enrolled', title: 'MFA', width: 100 },
          { field: 'account_type', title: 'Account Type', width: 130 },
          { field: 'status', title: 'Status', width: 120 },
        ],
      },
    };
    this.openDialog(configs[kpi]);
  }

  public onOpenPrivilegedDetail(): void {
    const organizationId = this.organizationId();
    if (organizationId == null) return;

    this.openDialog({
      title: 'Privileged Accounts',
      // One row per person, not per role assignment: the strip above counts people, so
      // listing assignments here would show more rows than the number it displays.
      subtitle: 'Every account with elevated permissions in your tenant.',
      apiUrl: this.itdrService.getDetailsUrl('privileged-accounts', organizationId),
      columns: [
        { field: 'identity', title: 'Identity', width: 220 },
        // Plural: 24 of 60 privileged principals in dev hold more than one role.
        { field: 'role', title: 'Roles', width: 260 },
        { field: 'can_reset_passwords', title: 'Resets Passwords', width: 150, type: 'boolean' },
        { field: 'can_grant_permissions', title: 'Grants Permissions', width: 160, type: 'boolean' },
        { field: 'can_create_tokens', title: 'Creates Tokens', width: 140, type: 'boolean' },
        // Registration, not enforcement - see the model. Values are "Registered
        // (methods)", "Not registered" or "Unknown".
        { field: 'mfa_status', title: 'MFA', width: 200 },
      ],
    });
  }

  public onOpenDetectionTrendDetail(): void {
    const organizationId = this.organizationId();
    if (organizationId == null) return;

    this.openDialog({
      title: 'Detections (last 30 days)',
      subtitle: 'All identity detections in the trend window.',
      apiUrl: this.itdrService.getDetailsUrl('detection-trend', organizationId),
      columns: [
        // Backfilled alerts carry a 64-character id: at 280 it wraps onto two lines.
        { field: 'detection_id', title: 'Detection ID', width: 280, type: 'id' },
        { field: 'case_id', title: 'Case ID', width: 100 },
        { field: 'identity', title: 'Identity', width: 220 },
        { field: 'category', title: 'Category', width: 180 },
        { field: 'severity', title: 'Severity', width: 110, type: 'badge' },
        { field: 'occurred_at', title: 'Occurred', width: 180, type: 'date' },
        { field: 'status', title: 'Status', width: 120 },
      ],
    });
  }

  public onEntityDialogClose(): void {
    this.showEntityDialog.set(false);
  }

  private openDialog(config: IdentityEntityDialogConfig): void {
    this.entityDialogConfig.set(config);
    this.showEntityDialog.set(true);
  }
}
